import { createFileRoute, Link as RouterLink } from '@tanstack/react-router';
import {
  Box,
  Input,
  Button,
  Typography,
  Container,
  Alert,
  Card,
  FormControl,
  FormLabel,
  FormHelperText,
  Link,
} from '@mui/joy';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import api from '@/services/axios';
import { pageTitle } from '@/utils/pageTitle';

const schema = z.object({
  email: z.string().email('Por favor ingrese un email válido'),
});

type FormData = z.infer<typeof schema>;

export const Route = createFileRoute('/_public/forgot-password')({
  component: ForgotPasswordPage,
  head: () => pageTitle('Recuperar contraseña'),
});

function ForgotPasswordPage() {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const mutation = useMutation({
    mutationFn: (email: string) =>
      api.post('/auth/forgot-password', { email }),
  });

  const onSubmit = (data: FormData) => {
    mutation.mutate(data.email);
  };

  return (
    <Container maxWidth='xs'>
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: 'calc(100vh - 64px)',
          justifyContent: 'center',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400, p: 4, boxShadow: 'lg' }}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography level='h2' fontWeight={500}>
              Recuperar contraseña
            </Typography>
            <Typography level='body-md'>
              Ingrese su correo y le enviaremos un enlace para restablecerla
            </Typography>
          </Box>

          <Box
            component='form'
            onSubmit={handleSubmit(onSubmit)}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <FormControl error={!!errors.email}>
                  <FormLabel>Correo electrónico</FormLabel>
                  <Input
                    {...field}
                    type='email'
                    placeholder='Ingrese su correo electrónico'
                    required
                    disabled={mutation.isPending}
                    autoComplete='email'
                    autoFocus
                  />
                  {errors.email && (
                    <FormHelperText>{errors.email.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {mutation.isSuccess && (
              <Alert color='success' className='text-center'>
                Si el correo está registrado, recibirá instrucciones para
                restablecer su contraseña.
              </Alert>
            )}

            {mutation.isError && (
              <Alert color='danger'>
                {(() => {
                  const err = mutation.error as AxiosError<{
                    errors?: Record<string, string>;
                  }>;
                  const data = err?.response?.data;
                  if (data?.errors) {
                    const e = data.errors;
                    return e.Error || e[Object.keys(e)[0]] || 'Error al enviar la solicitud.';
                  }
                  return 'Error al enviar la solicitud. Intente nuevamente.';
                })()}
              </Alert>
            )}

            <Button
              type='submit'
              fullWidth
              loading={mutation.isPending}
              variant='outlined'
              color='neutral'
              sx={{ mt: 1 }}
            >
              Enviar enlace
            </Button>

            <Typography level='body-sm' sx={{ textAlign: 'center' }}>
              <Link
                component={RouterLink}
                to='/login'
                sx={{ color: 'text.primary' }}
              >
                Volver al inicio de sesión
              </Link>
            </Typography>
          </Box>
        </Card>
      </Box>
    </Container>
  );
}
